import { Injectable, signal, effect, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { LoggerService } from './logger.service';

export type ThemeMode = 'light' | 'dark';
export type ThemeColor = 'base' | 'violet' | 'red' | 'blue' | 'orange' | 'yellow' | 'green';
export type ThemeDirection = 'ltr' | 'rtl';

export interface ThemeState {
  mode: ThemeMode;
  color: ThemeColor;
  direction: ThemeDirection;
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly logger = inject(LoggerService);
  private readonly STORAGE_KEY = 'theme';

  // Signal to store theme state
  public theme = signal<ThemeState>({
    mode: 'dark',
    color: 'blue',
    direction: 'ltr'
  });

  constructor() {
    // Load saved theme from localStorage
    this.loadTheme();

    // Effect to apply theme changes to DOM
    effect(() => {
      this.applyTheme();
    });
  }

  /** Valid enum values for runtime validation of localStorage data */
  private static readonly VALID_MODES: ReadonlySet<string> = new Set(['light', 'dark']);
  private static readonly VALID_COLORS: ReadonlySet<string> = new Set(['base', 'violet', 'red', 'blue', 'orange', 'yellow', 'green']);
  private static readonly VALID_DIRS: ReadonlySet<string> = new Set(['ltr', 'rtl']);

  /**
   * Load and validate theme from localStorage.
   * NOTE: The FOUC-prevention inline script in index.html reads the same
   * 'theme' key and validates against the same enum values. Keep them in sync.
   */
  private loadTheme(): void {
    try {
      const storage = this.document.defaultView?.localStorage;
      if (!storage) return;
      const savedTheme = storage.getItem(this.STORAGE_KEY);
      if (savedTheme) {
        const parsed = JSON.parse(savedTheme);
        if (
          parsed &&
          typeof parsed === 'object' &&
          ThemeService.VALID_MODES.has(parsed.mode) &&
          ThemeService.VALID_COLORS.has(parsed.color) &&
          ThemeService.VALID_DIRS.has(parsed.direction)
        ) {
          this.theme.set(parsed as ThemeState);
        } else {
          this.logger.warn('Invalid theme data in localStorage, using defaults', { component: 'ThemeService' });
        }
      }
    } catch (error) {
      this.logger.error('Failed to load theme from localStorage:', { component: 'ThemeService' }, error);
    }
  }

  private applyTheme(): void {
    const { mode, color, direction } = this.theme();
    const html = this.document.documentElement;

    // Apply dark/light mode
    if (mode === 'dark') {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }

    // Apply color theme
    html.setAttribute('data-theme', color);

    // Apply direction
    html.setAttribute('dir', direction);

    // Save to localStorage
    this.saveTheme();
  }

  private saveTheme(): void {
    try {
      const storage = this.document.defaultView?.localStorage;
      if (!storage) return;
      storage.setItem(this.STORAGE_KEY, JSON.stringify(this.theme()));
    } catch (error) {
      this.logger.error('Failed to save theme to localStorage:', { component: 'ThemeService' }, error);
    }
  }

  /**
   * Toggle between light and dark mode
   */
  public toggleDark(): void {
    this.theme.update(current => ({
      ...current,
      mode: current.mode === 'dark' ? 'light' : 'dark'
    }));
  }

  /**
   * Set the color theme
   */
  public setColor(color: ThemeColor): void {
    this.theme.update(current => ({
      ...current,
      color
    }));
  }

  /**
   * Set the text direction
   */
  public setDirection(direction: ThemeDirection): void {
    this.theme.update(current => ({
      ...current,
      direction
    }));
  }

  /**
   * Get whether dark mode is active
   */
  public get isDark(): boolean {
    return this.theme().mode === 'dark';
  }

  /**
   * Get the current color theme
   */
  public get currentColor(): ThemeColor {
    return this.theme().color;
  }

  /**
   * Get all available color options
   */
  public get colorOptions(): ThemeColor[] {
    return ['base', 'violet', 'red', 'blue', 'orange', 'yellow', 'green'];
  }

  /**
   * Get color swatch for UI display
   */
  public getColorSwatch(color: ThemeColor): string {
    const swatches: Record<ThemeColor, string> = {
      base: '#E11D48',
      violet: '#6E56CF',
      red: '#CC0033',
      blue: '#2490FF',
      orange: '#EA580C',
      yellow: '#FACC15',
      green: '#22C55E'
    };
    return swatches[color];
  }

  /**
   * Reset theme to defaults
   */
  public resetTheme(): void {
    this.theme.set({
      mode: 'dark',
      color: 'blue',
      direction: 'ltr'
    });
  }
}